import React, { Component } from "react";
import axios from "axios";
import { Card, FormControl, Grid, InputLabel, MenuItem, Select, Switch } from "@mui/material";
import { Link, Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

export default class Levels extends Component {
  state = {
    allJobs: [],
    allSubjects: [],
    allTopics: [],
    selectedSubjectId: "",
    selectedTopicId: "",
    selectedStatus: "all",
    togglingStatusIds: {},
    message: "",
    notification: false,
    noticolor: "success",
    pagination: {
      page: 1,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
      nextPage: null,
      prevPage: null,
      totalDocs: 0,
      limit: 20,
    },
    per_page: 20,
    page_no: 1,
    redirect: false,
  };

  notificationTimer = null;

  componentDidMount() {
    this.getSubjectOptions();
    this.getAllLevels(1, this.state.per_page);
  }

  componentWillUnmount() {
    if (this.notificationTimer) clearTimeout(this.notificationTimer);
  }

  showMessage = (message, color = "success") => {
    if (this.notificationTimer) clearTimeout(this.notificationTimer);
    this.setState({ notification: true, noticolor: color, message });
    this.notificationTimer = setTimeout(() => {
      this.setState({ notification: false, message: "" });
    }, 2500);
  };

  getSubjectOptions = async () => {
    if (init() !== "success") return;

    try {
      const resp = await axios.get(Constants.getUrls.getAllSubjects, {
        params: { page: 1, limit: 1000 },
      });
      this.setState({ allSubjects: resp?.data?.data?.docs || [] });
    } catch (error) {
      this.setState({ allSubjects: [] });
      this.showMessage(
        error?.response?.data?.message || "Unable to load subject filters.",
        "error"
      );
    }
  };

  getTopicOptions = async (subjectId) => {
    if (!subjectId || init() !== "success") {
      this.setState({ allTopics: [] });
      return;
    }

    try {
      const resp = await axios.get(Constants.getUrls.getAllTopics, {
        params: { subject_id: subjectId, page: 1, limit: 1000 },
      });
      this.setState({ allTopics: resp?.data?.data?.docs || [] });
    } catch (error) {
      this.setState({ allTopics: [] });
      this.showMessage(error?.response?.data?.message || "Unable to load topic filters.", "error");
    }
  };

  getAllLevels = async (page = 1, limit = this.state.per_page) => {
    if (init() !== "success") {
      window.location.assign("/signin");
      return;
    }

    const params = { page, limit };
    if (this.state.selectedSubjectId) params.subject_id = this.state.selectedSubjectId;
    if (this.state.selectedTopicId) params.topic_id = this.state.selectedTopicId;
    if (this.state.selectedStatus !== "all") params.status = this.state.selectedStatus;

    try {
      const resp = await axios.get(Constants.getUrls.getAllLevels, { params });
      if (resp?.data?.status !== "success") {
        this.showMessage(resp?.data?.message || "Unable to load levels.", "error");
        return;
      }

      const data = resp?.data?.data || {};
      if ((data.docs || []).length === 0 && page > 1 && Number(data.totalDocs || 0) > 0) {
        this.getAllLevels(page - 1, limit);
        return;
      }

      this.setState({
        allJobs: data.docs || [],
        pagination: data,
        page_no: data.page || page,
        per_page: data.limit || limit,
      });
    } catch (error) {
      this.showMessage(error?.response?.data?.message || "Unable to load levels.", "error");
    }
  };

  handleEntriesPerPageChange = (newLimit) => {
    this.setState({ per_page: newLimit }, () => this.getAllLevels(1, newLimit));
  };

  handlePageChange = (newPage) => {
    this.getAllLevels(newPage, this.state.per_page);
  };

  handleSubjectFilterChange = (event) => {
    const selectedSubjectId = event.target.value;
    this.setState(
      {
        selectedSubjectId,
        selectedTopicId: "",
        allTopics: [],
        page_no: 1,
      },
      () => {
        this.getTopicOptions(selectedSubjectId);
        this.getAllLevels(1, this.state.per_page);
      }
    );
  };

  handleTopicFilterChange = (event) => {
    this.setState({ selectedTopicId: event.target.value, page_no: 1 }, () => {
      this.getAllLevels(1, this.state.per_page);
    });
  };

  handleStatusFilterChange = (event) => {
    this.setState({ selectedStatus: event.target.value, page_no: 1 }, () => {
      this.getAllLevels(1, this.state.per_page);
    });
  };

  clearFilters = () => {
    this.setState(
      {
        selectedSubjectId: "",
        selectedTopicId: "",
        selectedStatus: "all",
        allTopics: [],
        page_no: 1,
      },
      () => this.getAllLevels(1, this.state.per_page)
    );
  };

  handleStatusToggle = async (level) => {
    if (!level?._id || init() !== "success") return;

    const nextStatus = level.status === "active" ? "inactive" : "active";
    if (
      nextStatus === "inactive" &&
      !window.confirm("Deactivate this level? It will be hidden from the app immediately.")
    ) {
      return;
    }

    this.setState((previousState) => ({
      togglingStatusIds: { ...previousState.togglingStatusIds, [level._id]: true },
    }));

    try {
      const resp = await axios.put(`${Constants.putUrls.updateLevelStatus}${level._id}`, {
        status: nextStatus,
      });
      this.showMessage(resp?.data?.message || "Level status updated successfully.");
      await this.getAllLevels(this.state.page_no, this.state.per_page);
    } catch (error) {
      this.showMessage(error?.response?.data?.message || "Unable to update level status.", "error");
    } finally {
      this.setState((previousState) => ({
        togglingStatusIds: { ...previousState.togglingStatusIds, [level._id]: false },
      }));
    }
  };

  handleDelete = async (id) => {
    if (init() !== "success") {
      window.location.assign("/signin");
      return;
    }

    try {
      const resp = await axios.delete(Constants.deleteUrls.deleteLevel + id);
      this.showMessage(resp?.data?.message || "Level deleted successfully.");
      await this.getAllLevels(this.state.page_no, this.state.per_page);
    } catch (error) {
      this.showMessage(error?.response?.data?.message || "Unable to delete level.", "error");
    }
  };

  render() {
    if (this.state.redirect) return <Navigate to="/signin" replace />;

    const hasFilters =
      this.state.selectedSubjectId ||
      this.state.selectedTopicId ||
      this.state.selectedStatus !== "all";

    const rows = this.state.allJobs.map((job, index) => ({
      serial: (this.state.page_no - 1) * this.state.per_page + index + 1,
      name: `${job.level_name || "-"} (${job.level_type || "-"})`,
      subject: job?.subject_id?.subject_name || "-",
      topic: job?.topic_id?.topic_name || "-",
      status: (
        <MDBox display="flex" alignItems="center" gap={1} minWidth="125px">
          <Switch
            checked={job.status === "active"}
            disabled={Boolean(this.state.togglingStatusIds[job._id])}
            onChange={() => this.handleStatusToggle(job)}
            color="success"
            inputProps={{ "aria-label": `Set ${job.level_name || "level"} status` }}
          />
          <span
            style={{
              color: job.status === "active" ? "#16a34a" : "#f59e0b",
              fontWeight: 700,
              textTransform: "capitalize",
            }}
          >
            {job.status === "active" ? "Active" : "Inactive"}
          </span>
        </MDBox>
      ),
      action: (
        <MDBox display="flex" gap={1} flexWrap="wrap" minWidth="220px">
          <Link to={`/level-questions/${job._id}`}>
            <MDButton variant="contained" size="small" color="info">
              Questions
            </MDButton>
          </Link>
          <Link to={`/updatelevel/${job._id}`}>
            <MDButton variant="outlined" size="small" color="success">
              Update
            </MDButton>
          </Link>
          <MDButton
            variant="outlined"
            size="small"
            color="error"
            onClick={() => this.handleDelete(job._id)}
          >
            Delete
          </MDButton>
        </MDBox>
      ),
    }));

    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Levels List
              </MDTypography>
            </MDBox>

            <MDBox px={2} pt={3} pb={1}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="level-subject-filter-label">Subject</InputLabel>
                    <Select
                      labelId="level-subject-filter-label"
                      value={this.state.selectedSubjectId}
                      label="Subject"
                      onChange={this.handleSubjectFilterChange}
                    >
                      <MenuItem value="">All subjects</MenuItem>
                      {this.state.allSubjects.map((subject) => (
                        <MenuItem key={subject._id} value={subject._id}>
                          {subject.subject_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" disabled={!this.state.selectedSubjectId}>
                    <InputLabel id="level-topic-filter-label">Topic</InputLabel>
                    <Select
                      labelId="level-topic-filter-label"
                      value={this.state.selectedTopicId}
                      label="Topic"
                      onChange={this.handleTopicFilterChange}
                    >
                      <MenuItem value="">All topics</MenuItem>
                      {this.state.allTopics.map((topic) => (
                        <MenuItem key={topic._id} value={topic._id}>
                          {topic.topic_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="level-status-filter-label">Status</InputLabel>
                    <Select
                      labelId="level-status-filter-label"
                      value={this.state.selectedStatus}
                      label="Status"
                      onChange={this.handleStatusFilterChange}
                    >
                      <MenuItem value="all">All statuses</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <MDBox
                    display="flex"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    gap={1}
                  >
                    <MDButton
                      variant="outlined"
                      size="small"
                      color="secondary"
                      disabled={!hasFilters}
                      onClick={this.clearFilters}
                    >
                      Clear filters
                    </MDButton>
                    <Link to="/addLevel">
                      <MDButton variant="contained" size="small" color="primary">
                        Create
                      </MDButton>
                    </Link>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>

            <MDBox>
              <DataTable
                table={{
                  columns: [
                    { Header: "serial", accessor: "serial", width: "6%" },
                    { Header: "name", accessor: "name" },
                    { Header: "subject", accessor: "subject" },
                    { Header: "topic", accessor: "topic" },
                    { Header: "status", accessor: "status" },
                    { Header: "action", accessor: "action" },
                  ],
                  rows,
                }}
                entriesPerPage={{ defaultValue: this.state.per_page, entries: [10, 25, 50, 100] }}
                page_no={this.state.pagination?.page}
                totalPages={this.state.pagination?.totalPages}
                hasNextPage={this.state.pagination?.hasNextPage}
                hasPrevPage={this.state.pagination?.hasPrevPage}
                isnextPage={this.state.pagination?.nextPage}
                prevPage={this.state.pagination?.prevPage}
                totalDocs={this.state.pagination?.totalDocs}
                onPageChange={this.handlePageChange}
                onEntriesPerPageChange={this.handleEntriesPerPageChange}
              />
            </MDBox>

            <MDSnackbar
              color={this.state.noticolor}
              icon="notifications"
              title="AgriQuiz"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
